import { useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";

// ─── Coloring shapes ───
const colorPalette = [
  { name: "أحمر", class: "bg-red-500", hex: "#ef4444" },
  { name: "أزرق", class: "bg-blue-500", hex: "#3b82f6" },
  { name: "أخضر", class: "bg-green-500", hex: "#22c55e" },
  { name: "أصفر", class: "bg-yellow-400", hex: "#facc15" },
  { name: "برتقالي", class: "bg-orange-500", hex: "#f97316" },
  { name: "بنفسجي", class: "bg-purple-500", hex: "#a855f7" },
  { name: "وردي", class: "bg-pink-500", hex: "#ec4899" },
  { name: "بني", class: "bg-amber-700", hex: "#b45309" },
];

const coloringPictures = [
  {
    name: "شمس ☀️",
    parts: [
      { id: "center", label: "وسط الشمس", emoji: "⭕", defaultColor: "#fde68a" },
      { id: "rays", label: "أشعة الشمس", emoji: "✨", defaultColor: "#fcd34d" },
      { id: "face", label: "وجه الشمس", emoji: "😊", defaultColor: "#fbbf24" },
    ],
  },
  {
    name: "بيت 🏠",
    parts: [
      { id: "walls", label: "جدران البيت", emoji: "🧱", defaultColor: "#d1d5db" },
      { id: "roof", label: "سقف البيت", emoji: "🔺", defaultColor: "#d1d5db" },
      { id: "door", label: "باب البيت", emoji: "🚪", defaultColor: "#d1d5db" },
      { id: "window", label: "نافذة البيت", emoji: "🪟", defaultColor: "#d1d5db" },
    ],
  },
  {
    name: "شجرة 🌳",
    parts: [
      { id: "trunk", label: "جذع الشجرة", emoji: "🪵", defaultColor: "#d1d5db" },
      { id: "leaves", label: "أوراق الشجرة", emoji: "🍃", defaultColor: "#d1d5db" },
      { id: "fruits", label: "ثمار الشجرة", emoji: "🍎", defaultColor: "#d1d5db" },
    ],
  },
  {
    name: "سمكة 🐟",
    parts: [
      { id: "body", label: "جسم السمكة", emoji: "🐟", defaultColor: "#d1d5db" },
      { id: "tail", label: "ذيل السمكة", emoji: "🔷", defaultColor: "#d1d5db" },
      { id: "eye", label: "عين السمكة", emoji: "👁️", defaultColor: "#d1d5db" },
    ],
  },
];

interface Props {
  character: string;
  characterName: string;
  onFinish: (score: number, maxScore: number) => void;
  onCorrect: () => void;
}

export default function ColoringGame({ character, characterName, onFinish, onCorrect }: Props) {
  const [currentPic, setCurrentPic] = useState(0);
  const [selectedColor, setSelectedColor] = useState(colorPalette[0]);
  const [partColors, setPartColors] = useState<Record<string, string>>({});
  const [completedPics, setCompletedPics] = useState(0);

  const pic = coloringPictures[currentPic];
  const totalPics = coloringPictures.length;

  const colorPart = (partId: string) => {
    const newColors = { ...partColors, [`${currentPic}-${partId}`]: selectedColor.hex };
    setPartColors(newColors);
    
    // Check if all parts colored
    const allColored = pic.parts.every(p => newColors[`${currentPic}-${p.id}`] && newColors[`${currentPic}-${p.id}`] !== "#d1d5db");
    if (allColored) {
      onCorrect();
      const next = completedPics + 1;
      setCompletedPics(next);
      if (next >= totalPics) {
        setTimeout(() => onFinish(totalPics, totalPics), 1000);
      } else {
        setTimeout(() => setCurrentPic(currentPic + 1), 1200);
      }
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <Badge className="bg-pink-100 text-pink-800 border-pink-300 text-sm">
        الرسمة: {currentPic + 1} / {totalPics}
      </Badge>
      <Card className="bg-white/90 backdrop-blur-sm border-2 border-pink-200 shadow-xl rounded-2xl">
        <CardContent className="p-5 space-y-4 text-center">
          <div className="flex items-center justify-center gap-3">
            <img src={character} alt={characterName} className="w-14 h-14 rounded-full border-2 border-pink-300 bg-pink-50 object-cover" />
            <p className="text-sm font-semibold text-pink-700">{characterName} تقول: لوّن الرسمة! 🎨</p>
          </div>

          <h3 className="text-xl font-bold text-foreground">{pic.name}</h3>

          {/* Canvas - parts to color */}
          <div className="grid grid-cols-2 gap-3">
            {pic.parts.map((part) => {
              const currentColor = partColors[`${currentPic}-${part.id}`] || part.defaultColor;
              return (
                <motion.button
                  key={part.id}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => colorPart(part.id)}
                  className="p-4 rounded-2xl border-3 border-dashed border-gray-300 transition-all hover:border-pink-400 shadow-sm"
                  style={{ backgroundColor: currentColor }}
                >
                  <span className="text-3xl block mb-1">{part.emoji}</span>
                  <span className="text-xs font-bold text-gray-700 bg-white/70 rounded-full px-2 py-0.5">{part.label}</span>
                </motion.button>
              );
            })}
          </div>

          {/* Color palette */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2">🎨 اختر اللون:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {colorPalette.map((color) => (
                <button
                  key={color.name}
                  onClick={() => setSelectedColor(color)}
                  className={`w-10 h-10 rounded-full border-3 transition-all shadow-sm hover:scale-110 ${
                    selectedColor.name === color.name ? "border-foreground scale-110 ring-2 ring-offset-2 ring-foreground" : "border-white"
                  } ${color.class}`}
                  title={color.name}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">اللون المختار: <strong>{selectedColor.name}</strong></p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
